import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import moment from "moment";
import Loader from "../Loader/Loader";
import WebCard from "../Card/WebCard";
import MobileCard from "../Card/MobileCard";
import { serviceConsumer } from "../../network/ServiceConsumer";
import { buildEventFeed, matchesSearch, resultsOnlyFeed } from "../../helper/eventFeed";
import { Msr, useMaterialSymbols } from "../../Teacher/Oireachtas/components";
import "../Card/style.css";
import "../../Teacher/Oireachtas/oireachtas.css";
import "./EventFeed.css";

// The single event list on the home page (logged out) and the parent
// dashboard: happening today first, then future events soonest first, then
// past results newest first. Card shadows and labels carry each event's state.
// Styled in the .oir design language (the Oireachtas organizer panel).
// resultsOnly (the Results page): only events with at least one published
// result (Devin 2026-10-31): those running now under Happening now, the rest
// as results cards (resultsOnlyFeed).
// upcomingOnly (the signed-in Events page): events running now (first day to
// midnight of the last) and future events; finished events live on the
// Results page (Devin 2026-09-25). An event running now with a published
// result is on both pages (Devin 2026-10-31).
// searchHeading: a section title above the search (the signed-in Events page:
// "Events", separating the search from the Entered cards above it); the
// future list below is then headed "Upcoming".
// lead: content shown above the search in the same .oir scope (the parent
// dashboard's Entered cards). hideEventIds: events already shown there, left
// out of the feed so each event appears once (Devin 2026-09-25).
// loadBuckets(date): a different source for the listings (the signed-in
// Results page loads only the viewer's own events); emptyText overrides the
// empty-feed message. resultsTab: the tab each results card opens on (the
// Results page's My adjudications view: "judged").
// Scrolling to the bottom loads the next events, then the past results
// (a "Show more" button stands in where IntersectionObserver is missing).

const LISTINGS = {
  open: "getOpenEvents",
  upcoming: "getupcomingEvents",
  closed: "getCloseEvents",
  results: "getResultsEvents",
};

const FUTURE_STEP = 8;
const PAST_STEP = 8;
const canObserve = () => typeof window !== "undefined" && "IntersectionObserver" in window;

// An empty listing answers HTTP 400 "There are no events"; treat any failed
// listing as empty so one bucket can't blank the whole feed.
const fetchListing = async (endpoint, date) => {
  try {
    const url = `${process.env.REACT_APP_BASE_URL}/feis/${endpoint}`;
    // `date`: wall clock labelled UTC (calendar-day feis dates); `now`: the
    // real instant (Oireachtas close deadlines in the event's time zone)
    const response = await serviceConsumer("POST", url, { page: 1, limit: 1000, date, now: new Date().toISOString() });
    return response?.Feis?.results || [];
  } catch (error) {
    return [];
  }
};

const FeedCard = ({ item, isHomePage, resultsTab }) => (
  <div className={`register-events feed-card feed-card--${item.state}`} data-value={item.event._id}>
    <WebCard event={item.event} from={item.from} feedState={item.state} isHomePage={isHomePage} resultsTab={resultsTab} />
    <MobileCard event={item.event} from={item.from} feedState={item.state} isHomePage={isHomePage} resultsTab={resultsTab} />
  </div>
);

export const loadListings = async (date) => {
  const keys = Object.keys(LISTINGS);
  const lists = await Promise.all(keys.map((k) => fetchListing(LISTINGS[k], date)));
  const buckets = {};
  keys.forEach((k, i) => { buckets[k] = lists[i]; });
  return buckets;
};

const EventFeed = ({
  isHomePage = false, resultsOnly = false, upcomingOnly = false, lead = null, hideEventIds = [], searchHeading,
  loadBuckets = loadListings, emptyText, resultsTab,
}) => {
  useMaterialSymbols();
  const [loading, setLoading] = useState(true);
  const [feed, setFeed] = useState({ today: [], future: [], past: [] });
  const [searchInput, setSearchInput] = useState("");
  const [futureShown, setFutureShown] = useState(FUTURE_STEP);
  const [pastShown, setPastShown] = useState(PAST_STEP);
  const sentinel = useRef(null);

  useEffect(() => {
    let cancelled = false;
    // Same "now" the tabs sent: local wall-clock time expressed as UTC.
    const date = moment(new Date()).utc(true).format();
    loadBuckets(date)
      .catch(() => ({}))
      .then((buckets) => {
        if (cancelled) return;
        setFeed(buildEventFeed(buckets, new Date()));
        setLoading(false);
      });
    return () => { cancelled = true; };
    // the source is fixed for the page's life
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const hiddenKey = hideEventIds.map(String).sort().join(",");
  const shown = useMemo(() => {
    const hidden = new Set(hiddenKey ? hiddenKey.split(",") : []);
    const keep = (i) => !hidden.has(String(i.event?._id));
    const base = resultsOnly ? resultsOnlyFeed(feed) : feed;
    return { today: base.today.filter(keep), future: base.future.filter(keep), past: base.past.filter(keep) };
  }, [feed, hiddenKey, resultsOnly]);

  const filtered = useMemo(() => ({
    today: shown.today.filter((i) => matchesSearch(i, searchInput)),
    future: resultsOnly ? [] : shown.future.filter((i) => matchesSearch(i, searchInput)),
    past: upcomingOnly ? [] : shown.past.filter((i) => matchesSearch(i, searchInput)),
  }), [shown, searchInput, resultsOnly, upcomingOnly]);

  // Past results follow once every future event is on screen.
  const futureDone = filtered.future.length <= futureShown;
  const moreFuture = !futureDone;
  const morePast = futureDone && filtered.past.length > pastShown;
  const loadMore = useCallback(() => {
    if (moreFuture) setFutureShown((n) => n + FUTURE_STEP);
    else if (morePast) setPastShown((n) => n + PAST_STEP);
  }, [moreFuture, morePast]);

  useEffect(() => {
    if (!canObserve() || !sentinel.current || !(moreFuture || morePast)) return undefined;
    const io = new IntersectionObserver((entries) => {
      if (entries[0].isIntersecting) loadMore();
    }, { rootMargin: "400px 0px" });
    io.observe(sentinel.current);
    return () => io.disconnect();
  }, [loadMore, moreFuture, morePast]);

  const total = shown.today.length + (resultsOnly ? 0 : shown.future.length) + (upcomingOnly ? 0 : shown.past.length);
  const matched = filtered.today.length + filtered.future.length + filtered.past.length;

  return (
    <section className="oir feed">
      {lead}
      {searchHeading && (
        <div className="feed-heading feed-search-head" role="heading" aria-level="2">{searchHeading}</div>
      )}
      <div className="feed-toolbar">
        <label className="search">
          <Msr>search</Msr>
          <input
            type="text"
            name="search"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search events or cities"
            aria-label="Search events"
          />
        </label>
      </div>

      {loading && <Loader />}

      {!loading && total === 0 && <div className="feed-empty">{emptyText || (resultsOnly ? "No results yet." : "No events right now.")}</div>}
      {!loading && total > 0 && matched === 0 && (
        <div className="feed-empty">There are no events matching your search.</div>
      )}

      {!loading && filtered.today.length > 0 && (
        <div className="feed-section feed-section--today">
          <div className="feed-heading" role="heading" aria-level="2">Happening now</div>
          {filtered.today.map((item) => <FeedCard key={item.event._id} item={item} isHomePage={isHomePage} resultsTab={resultsTab} />)}
        </div>
      )}

      {!loading && filtered.future.length > 0 && (
        <div className="feed-section">
          <div className="feed-heading" role="heading" aria-level="2">{searchHeading ? "Upcoming" : "Events"}</div>
          {filtered.future.slice(0, futureShown).map((item) => (
            <FeedCard key={item.event._id} item={item} isHomePage={isHomePage} resultsTab={resultsTab} />
          ))}
          {moreFuture && !canObserve() && (
            <button className="feed-more" onClick={loadMore}>
              Show more events
            </button>
          )}
        </div>
      )}

      {!loading && futureDone && filtered.past.length > 0 && (
        <div className="feed-section">
          <div className="feed-heading" role="heading" aria-level="2">Results</div>
          {filtered.past.slice(0, pastShown).map((item) => (
            <FeedCard key={item.event._id} item={item} isHomePage={isHomePage} resultsTab={resultsTab} />
          ))}
          {morePast && !canObserve() && (
            <button className="feed-more" onClick={loadMore}>
              Show more results
            </button>
          )}
        </div>
      )}
      {!loading && <div ref={sentinel} className="feed-sentinel" aria-hidden="true" />}
    </section>
  );
};

export default EventFeed;
